import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiAlertTriangle,
  FiCalendar,
  FiUser,
  FiRefreshCw,
  FiChevronDown,
  FiLoader,
  FiCheck,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiPhoneCall,
  FiPhoneOff,
} from "react-icons/fi";
import api from "../../../connector/api";
import BehaviorLogModal from "../modal/BehaviorLogModal";
import BehaviorLogDetailModal from "../modal/BehaviorLogDetailModal";
import Pagination from "../reuse/Pagination";

const FILTER_OPTIONS = [
  { value: "ALL", label: "All Categories" },
  { value: "POSITIVE", label: "Positive / Praise" },
  { value: "CONCERN", label: "Behavioral Concern" },
  { value: "ACADEMIC", label: "Academic" },
  { value: "NEUTRAL", label: "Neutral / Observation" },
  { value: "OTHER", label: "Other" },
];

/* Custom Filter Dropdown */
const CustomFilterSelect = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors cursor-pointer min-w-[150px]"
      >
        <span className="truncate">{selectedOption ? selectedOption.label : "Select..."}</span>
        <FiChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-1 z-30 w-48 bg-white border border-zinc-200 rounded-lg shadow-lg py-1 text-xs">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-50 transition-colors cursor-pointer ${
                value === opt.value
                  ? "bg-blue-50/50 text-blue-700 font-bold"
                  : "text-zinc-700"
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && (
                <FiCheck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function BehaviorLogTab({ selectedClassroom, subjectAssignmentId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  /* Detail Modal State */
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  /* Delete Confirmation Modal State */
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedInfraction, setSelectedInfraction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /* Toggle parent contact state tracking */
  const [updatingParentContactId, setUpdatingParentContactId] = useState(null);

  /* Pagination State */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* Toast Notification State & Handler */
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3500);
  }, []);

  const fetchBehaviorLogs = async () => {
    setLoading(true);
    try {
      let url = "/discipline-referrals/";
      const queryParams = new URLSearchParams();

      if (selectedClassroom?.id) {
        queryParams.append("classroom", selectedClassroom.id);
      }
      if (subjectAssignmentId) {
        queryParams.append("subject_assignment_id", subjectAssignmentId);
      }

      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const res = await api.get(url);
      const data = res.data?.results || res.data || [];
      setLogs(data);
    } catch (err) {
      console.error("Failed to fetch behavior logs:", err);
      showToast("Failed to load behavior logs", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBehaviorLogs();
  }, [selectedClassroom, subjectAssignmentId]);

  /* Reset page on filter changes */
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);

  const handleOpenCreateModal = () => {
    setSelectedRecord(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (e, item) => {
    e.stopPropagation(); // Prevents row click detail modal trigger
    setSelectedRecord(item);
    setIsModalOpen(true);
  };

  const handleOpenDetailModal = (item) => {
    setDetailRecord(item);
    setIsDetailModalOpen(true);
  };

  const handlePromptDelete = (e, item) => {
    e.stopPropagation(); // Prevents row click detail modal trigger
    const studentName =
      item.student_detail?.full_name ||
      `${item.student_detail?.first_name || ""} ${item.student_detail?.last_name || ""}`.trim() ||
      (typeof item.student === "object" ? item.student?.name : null) ||
      `Student #${item.student}`;

    setSelectedInfraction({
      id: item.id,
      student_name: studentName,
    });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteInfraction = async () => {
    if (!selectedInfraction?.id) return;
    setSubmitting(true);
    try {
      await api.delete(`/discipline-referrals/${selectedInfraction.id}/`);
      setLogs((prev) => prev.filter((item) => item.id !== selectedInfraction.id));
      setIsDeleteModalOpen(false);
      setSelectedInfraction(null);
      showToast("Behavior log deleted successfully", "success");
    } catch (err) {
      console.error("Failed to delete behavior log:", err);
      showToast("Failed to delete record", "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* Toggle Parent Contacted Handler */
  const handleToggleParentContact = async (e, item) => {
    e.stopPropagation(); // Prevent modal from opening on click
    const nextStatus = !item.parent_contacted;
    setUpdatingParentContactId(item.id);

    try {
      await api.patch(`discipline-referrals/${item.id}/toggle_parent_contact/`, {
        parent_contacted: nextStatus,
      });

      setLogs((prev) =>
        prev.map((log) =>
          log.id === item.id ? { ...log, parent_contacted: nextStatus } : log
        )
      );

      showToast(
        nextStatus
          ? "Marked parent as contacted"
          : "Marked parent as not contacted",
        "success"
      );
    } catch (err) {
      console.error("Failed to update parent contact status:", err);
      showToast("Failed to update parent contact status", "error");
    } finally {
      setUpdatingParentContactId(null);
    }
  };

  const handleModalSuccess = () => {
    fetchBehaviorLogs();
    showToast(
      selectedRecord ? "Record updated successfully" : "Behavior log saved successfully",
      "success"
    );
  };

  // Filter Search & Category logic
  const filteredLogs = logs.filter((item) => {
    const studentName =
      item.student_detail?.full_name ||
      `${item.student_detail?.first_name || ""} ${item.student_detail?.last_name || ""}`.trim() ||
      (typeof item.student === "object" ? item.student?.name : null) ||
      `Student #${item.student}`;

    const matchesSearch =
      studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.behavior_type && item.behavior_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.notes && item.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.location && item.location.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType =
      typeFilter === "ALL" || item.behavior_type === typeFilter;

    return matchesSearch && matchesType;
  });

  // Pagination Slicing
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, startIndex + itemsPerPage);

  const getBehaviorBadge = (type) => {
    switch (type) {
      case "POSITIVE":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "CONCERN":
        return "bg-rose-50 text-rose-700 border-rose-200";
      case "ACADEMIC":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "NEUTRAL":
        return "bg-zinc-100 text-zinc-700 border-zinc-200";
      default:
        return "bg-blue-50 text-blue-700 border-blue-200";
    }
  };

  return (
    <>
      {/* Top Right Toast Notification Portal */}
      {toast.show &&
        createPortal(
          <div className="fixed top-5 right-5 z-[10000] flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-xl border bg-white animate-in fade-in slide-in-from-top-2 duration-200 max-w-sm">
            {toast.type === "error" ? (
              <FiXCircle className="w-5 h-5 text-rose-600 shrink-0" />
            ) : (
              <FiCheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
            )}
            <span className="text-xs font-medium text-zinc-800 flex-1">
              {toast.message}
            </span>
            <button
              type="button"
              onClick={() => setToast((prev) => ({ ...prev, show: false }))}
              className="p-1 text-zinc-400 hover:text-zinc-600 rounded transition-colors cursor-pointer"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>,
          document.body
        )}

      {/* Delete Confirmation Portal */}
      {isDeleteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs">
            <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xl w-full max-w-sm p-5 space-y-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto">
                <FiTrash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-zinc-900">
                  Delete Infraction Record
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  Are you sure you want to delete the record for{" "}
                  <span className="font-bold text-zinc-800">
                    {selectedInfraction?.student_name || "this student"}
                  </span>
                  ? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="w-full py-1.5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteInfraction}
                  disabled={submitting}
                  className="w-full py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {submitting && <FiLoader className="w-3.5 h-3.5 animate-spin" />}
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <div className="space-y-4 font-dynamic-title">
        {/* Action Bar */}
        <div className="flex flex-col sm:flex-row font-dynamic-title items-center justify-between gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Search Box */}
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-2.5 w-3.5 h-3.5 text-zinc-400" />
              <input
                type="text"
                placeholder="Search student, observations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs font-medium text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
              />
            </div>

            {/* Custom Filter Select Dropdown */}
            <CustomFilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              options={FILTER_OPTIONS}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleOpenCreateModal}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors shadow-xs cursor-pointer"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Log Behavior</span>
            </button>
          </div>
        </div>

        {/* Behavior Logs Table */}
        <div className="bg-white border border-zinc-200/80 rounded-lg shadow-xs overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-zinc-400 text-xs flex flex-col items-center gap-2">
              <FiRefreshCw className="w-5 h-5 animate-spin" />
              <span>Loading behavior logs...</span>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-12 text-center text-zinc-400 text-xs flex flex-col items-center gap-2">
              <FiAlertTriangle className="w-6 h-6 text-zinc-300" />
              <span>No behavior logs found.</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Observations / Notes</th>
                    <th className="px-4 py-3">Date & Time</th>
                    <th className="px-4 py-3 text-center">Parent Contacted</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-xs">
                  {paginatedLogs.map((item) => {
                    const studentName =
                      item.student_detail?.full_name ||
                      `${item.student_detail?.last_name || ""}, ${item.student_detail?.first_name || ""}`.trim() ||
                      (typeof item.student === "object" ? item.student?.name : null) ||
                      `Student #${item.student}`;

                    const isUpdatingContact = updatingParentContactId === item.id;

                    return (
                      <tr
                        key={item.id}
                        onClick={() => handleOpenDetailModal(item)}
                        className="hover:bg-zinc-50/80 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3 font-medium text-zinc-800">
                          <div className="flex items-center gap-2">
                            <FiUser className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            <span className="truncate">{studentName}</span>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getBehaviorBadge(
                              item.violation_type
                            )}`}
                          >
                            {item.violation_type === "OTHER" && item.custom_behavior_type
                              ? item.custom_behavior_type
                              : item.violation_type || "GENERAL"}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-zinc-600 max-w-xs truncate">
                          {item.notes || item.incident_description || "—"}
                        </td>

                        <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <FiCalendar className="w-3 h-3 text-zinc-400" />
                            <span>{item.log_date || item.date_of_incident}</span>
                            {item.log_time && (
                              <span className="text-zinc-400">({item.log_time})</span>
                            )}
                          </div>
                        </td>

                        {/* Interactive Parent Contact Toggle Button Column */}
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => handleToggleParentContact(e, item)}
                            disabled={isUpdatingContact}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all cursor-pointer ${
                              item.parent_contacted
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                                : "bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200/80"
                            }`}
                            title={
                              item.parent_contacted
                                ? "Click to mark parent NOT contacted"
                                : "Click to mark parent contacted"
                            }
                          >
                            {isUpdatingContact ? (
                              <FiLoader className="w-3 h-3 animate-spin text-zinc-500" />
                            ) : item.parent_contacted ? (
                              <FiPhoneCall className="w-3 h-3 text-emerald-600 shrink-0" />
                            ) : (
                              <FiPhoneOff className="w-3 h-3 text-zinc-400 shrink-0" />
                            )}
                            <span>{item.parent_contacted ? "Contacted" : "Not Contacted"}</span>
                          </button>
                        </td>

                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={(e) => handleOpenEditModal(e, item)}
                              className="p-1.5 text-zinc-500 hover:text-zinc-900 rounded-md hover:bg-zinc-200/60 transition-colors cursor-pointer"
                              title="Edit Log"
                            >
                              <FiEdit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handlePromptDelete(e, item)}
                              className="p-1.5 text-zinc-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                              title="Delete Log"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Footer */}
          {!loading && filteredLogs.length > 0 && (
            <div className="p-3 border-t border-zinc-200/80 bg-zinc-50/50">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>

        {/* Behavior Log Modal (Create / Edit) */}
        <BehaviorLogModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedRecord={selectedRecord}
          classId={selectedClassroom?.id}
          subjectAssignmentId={subjectAssignmentId}
          onSuccess={handleModalSuccess}
        />

        {/* Behavior Log Detail Modal */}
        <BehaviorLogDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          record={detailRecord}
        />
      </div>
    </>
  );
}